import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faChalkboardTeacher,
  faRunning,
  faUser,
  faBus,
  faHashtag,
  faHashtag as faBibNumber,
} from "@fortawesome/free-solid-svg-icons";

const SHUTTLE_COLLECTION = "pendaftaranShuttle2026";

// Utility untuk caching
const cachePesananDetail = (uid, eventId, orderId, data) => {
  try {
    localStorage.setItem(
      `pesanan_detail_${uid}_${eventId}_${orderId}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("[CACHE] Gagal menyimpan cache:", err);
  }
};

const getCachedPesananDetail = (uid, eventId, orderId) => {
  try {
    const cached = localStorage.getItem(
      `pesanan_detail_${uid}_${eventId}_${orderId}`
    );
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000; // 5 menit
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_detail_${uid}_${eventId}_${orderId}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("[CACHE] Gagal mengambil cache:", err);
    localStorage.removeItem(`pesanan_detail_${uid}_${eventId}_${orderId}`);
    return null;
  }
};

// --- Definisi Komponen ---
export const RegistrationDetail = React.memo(() => {
  // --- State dan Variabel ---
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pesanan, setPesanan] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const eventId = searchParams.get("eventId");
  const orderId = searchParams.get("orderId");
  const status = searchParams.get("transaction_status");

  // --- Fungsi Pembantu ---
  const formatDate = useCallback((date) => {
    if (!date) return "Tidak tersedia";
    try {
      if (date && (date.seconds || date._seconds)) {
        const timestamp = date.seconds || date._seconds;
        return new Date(timestamp * 1000).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (typeof date === "string") {
        let parsedDate = new Date(date.replace(" at ", ""));
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
        parsedDate = new Date(date.replace(/WIB$/, "").trim());
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      console.warn("[FORMAT] Format tanggal tidak dikenali:", date);
      return "Tidak tersedia";
    } catch (err) {
      console.error("[FORMAT] Gagal memformat tanggal:", err, date);
      return "Tidak tersedia";
    }
  }, []);

  const formatCategory = useCallback((pesananData, eventData) => {
    if (eventData?.type === "seminar" || eventData?.type === "membership") {
      return null;
    }
    if (eventData?.type === "shuttle") {
      return null; // Tidak perlu kategori untuk shuttle
    }
    if (pesananData?.kategori && pesananData.kategori !== "") {
      if (pesananData.kategori.includes("-")) {
        return pesananData.kategori.split("-")[1].trim();
      }
      return pesananData.kategori;
    }
    if (eventData?.categories?.length > 0) {
      const category =
        eventData.categories.find(
          (cat) => cat.distance === pesananData?.distance
        ) || eventData.categories[0];
      const parts = [];
      if (category?.distance) parts.push(`${category.distance} km`);
      if (category?.cot) parts.push(`COT ${category.cot} jam`);
      return parts.join(", ") || "Tidak diketahui";
    }
    return "Tidak diketahui";
  }, []);

  const fetchPesananDetail = useCallback(
    async (forceRefresh = true) => {
      if (
        !user?.uid ||
        !eventId ||
        !orderId ||
        fetchStatus.current.isFetching
      ) {
        if (!user?.uid)
          setError("Pengguna tidak terautentikasi. Silakan login kembali.");
        if (!eventId || !orderId)
          setError("Parameter eventId atau orderId tidak valid.");
        if (fetchStatus.current.isFetching) {
          console.warn("[FETCH] Fetch sedang berlangsung, melewati:", {
            eventId,
            orderId,
            userUid: user?.uid,
          });
          return;
        }
        console.error("[FETCH] Validasi gagal:", {
          userUid: user?.uid,
          eventId,
          orderId,
        });
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setError(""); // Reset error state

        // Cek cache
        if (!forceRefresh) {
          const cachedData = getCachedPesananDetail(user.uid, eventId, orderId);
          if (cachedData && isMounted.current) {
            setPesanan(cachedData.pesanan);
            setEventData(cachedData.eventData);
            setUserData(cachedData.userData);
            console.log("[FETCH] Menggunakan data dari cache:", {
              eventId,
              orderId,
            });
            return;
          }
        }

        // Hapus cache lama jika forceRefresh
        if (forceRefresh) {
          localStorage.removeItem(
            `pesanan_detail_${user.uid}_${eventId}_${orderId}`
          );
        }

        let pesananData = null;
        let eventData = null;
        let userData = null;
        let collectionName = "";

        // Tentukan koleksi berdasarkan eventId
        if (eventId === "ALTI_MEMBERSHIP") {
          collectionName = "daftarAnggotaAlti";
          eventData = {
            title: "Keanggotaan ALTI",
            location: "Jakarta",
            eventDateTimestamp: null,
            type: "membership",
          };
        } else if (eventId === "JOTR") {
          // Cek apakah ini shuttle order
          const shuttleDocRef = doc(db, SHUTTLE_COLLECTION, orderId);
          const shuttleDoc = await getDoc(shuttleDocRef);
          if (shuttleDoc.exists()) {
            collectionName = SHUTTLE_COLLECTION;
            eventData = {
              title: "Jakarta Open Trail Run - Shuttle",
              location: "Jakarta",
              eventDateTimestamp: null,
              type: "shuttle",
            };
          } else {
            // Bukan shuttle, cek event JOTR
            const eventDocRef = doc(db, "semuaKegiatan", eventId);
            const eventDoc = await getDoc(eventDocRef);
            if (!eventDoc.exists()) {
              throw new Error(
                `Event ${eventId} tidak ditemukan di semuaKegiatan.`
              );
            }
            eventData = eventDoc.data();
            collectionName =
              eventData.collectionName || `pendaftaran${eventId}`;
            eventData.type = eventData.type || "trail_running";
            if (eventData.categories?.length > 0) {
              eventData.eventDate = eventData.categories[0].date || null;
            }
            eventData.eventDateTimestamp = eventData.eventDateTimestamp || null;
          }
        } else {
          // Event lain (non-JOTR, non-membership)
          const eventDocRef = doc(db, "semuaKegiatan", eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (!eventDoc.exists()) {
            throw new Error(
              `Event ${eventId} tidak ditemukan di semuaKegiatan.`
            );
          }
          eventData = eventDoc.data();
          collectionName = eventData.collectionName || `pendaftaran${eventId}`;
          eventData.type = eventData.type || "trail_running";
          if (eventData.categories?.length > 0) {
            eventData.eventDate = eventData.categories[0].date || null;
          }
          eventData.eventDateTimestamp = eventData.eventDateTimestamp || null;
        }

        // Ambil data pesanan
        console.log("[FETCH] Querying collection:", {
          collectionName,
          orderId,
          userUid: user.uid,
        });
        const pesananDocRef = doc(db, collectionName, orderId);
        const pesananDoc = await getDoc(pesananDocRef);
        if (!pesananDoc.exists()) {
          console.error("[FETCH] Dokumen pesanan tidak ditemukan:", {
            collectionName,
            orderId,
          });
          throw new Error(
            "Pesanan tidak ditemukan atau Anda tidak memiliki akses."
          );
        }
        pesananData = pesananDoc.data();
        if (pesananData.uid !== user.uid) {
          console.error("[FETCH] UID tidak cocok:", {
            documentUid: pesananData.uid,
            userUid: user.uid,
          });
          throw new Error("Akses ditolak: Anda tidak memiliki izin.");
        }

        // Ambil data pengguna dari koleksi users
        const userDocRef = doc(db, "users", pesananData.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          userData = userDoc.data();
        } else {
          console.warn(
            "[FETCH] Data pengguna tidak ditemukan untuk uid:",
            pesananData.uid
          );
        }

        if (isMounted.current) {
          const pesananFormatted = {
            id: pesananDoc.id,
            eventId,
            collectionName,
            kategori: formatCategory(pesananData, eventData),
            harga: pesananData.harga || 0,
            transaction_status:
              pesananData.transaction_status || pesananData.status || "pending",
            namaLengkap:
              pesananData.namaLengkap ||
              userData?.namaLengkap ||
              "Tidak tersedia",
            email: pesananData.email || userData?.email || "Tidak tersedia",
            telepon:
              pesananData.telepon || userData?.telepon || "Tidak tersedia",
            orderId: pesananData.orderId || pesananDoc.id,
            pickupPoint: pesananData.pickupPoint || null,
            departureTime: pesananData.departureTime || null,
            returnPoint: pesananData.returnPoint || null,
            returnTime: pesananData.returnTime || null,
            bibNumber: pesananData.bibNumber || "Tidak tersedia",
            bibName: pesananData.bibName || null,
            vehiclePlateNumber: pesananData.vehiclePlateNumber || null,
            ...pesananData,
          };
          setPesanan(pesananFormatted);
          setEventData(eventData);
          setUserData(userData);
          cachePesananDetail(user.uid, eventId, orderId, {
            pesanan: pesananFormatted,
            eventData,
            userData,
          });
          console.log("[FETCH] Pesanan berhasil diambil:", {
            eventId,
            orderId,
            collectionName,
            pesananData,
            userData,
          });
        }
      } catch (err) {
        if (isMounted.current) {
          setError(
            err.message.includes("permission-denied") ||
              err.message.includes("insufficient permissions")
              ? "Akses ditolak: Anda tidak memiliki izin untuk melihat pesanan ini."
              : err.message ||
                  "Gagal memuat detail pesanan. Silakan coba lagi nanti."
          );
          showNotification(
            "error",
            err.message.includes("permission-denied") ||
              err.message.includes("insufficient permissions")
              ? "Akses ditolak: Anda tidak memiliki izin."
              : err.message || "Gagal memuat detail pesanan."
          );
          console.error("[FETCH] Error di fetchPesananDetail:", err, {
            eventId,
            orderId,
            userUid: user.uid,
          });
        }
      } finally {
        fetchStatus.current.isFetching = false;
      }
    },
    [user, eventId, orderId, showNotification, formatCategory]
  );

  const handleBayar = useCallback(
    (snapToken) => {
      if (snapToken && window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: () => {
            showNotification("success", "Pembayaran berhasil!");
            fetchPesananDetail(true);
          },
          onPending: () => {
            showNotification("warning", "Pembayaran sedang diproses.");
            fetchPesananDetail(true);
          },
          onError: () => {
            setError("Terjadi kesalahan saat pembayaran.");
            showNotification("error", "Terjadi kesalahan saat pembayaran.");
          },
          onClose: () => {},
        });
      } else {
        setError("Token pembayaran atau Midtrans tidak ditemukan.");
        showNotification(
          "error",
          "Token pembayaran atau Midtrans tidak ditemukan."
        );
        console.error("[PAYMENT] Tidak ada snapToken atau window.snap:", {
          snapToken,
        });
      }
    },
    [showNotification, fetchPesananDetail]
  );

  // --- Efek Samping (useEffect) ---
  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Silakan login untuk melihat detail pesanan.");
      return;
    }

    fetchPesananDetail(true);

    if (status) {
      if (status === "settlement") {
        setMessage({ type: "success", text: "Pembayaran berhasil!" });
        showNotification("success", "Pembayaran berhasil!");
      } else if (status === "pending") {
        setMessage({ type: "warning", text: "Harap tunggu konfirmasi." });
        showNotification("warning", "Harap tunggu konfirmasi.");
      } else if (status === "failed") {
        setMessage({
          type: "error",
          text: "Pembayaran gagal. Silakan coba lagi.",
        });
        showNotification("error", "Pembayaran gagal.");
      }
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, status, showNotification, fetchPesananDetail, navigate]);

  // --- Render JSX ---
  return (
    <section className="min-h-screen bg-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[450px]">
        {/* Notifikasi */}
        <AnimatePresence>
          {message && (
            <div
              className={`fixed top-4 right-4 p-4 rounded-xl flex items-center space-x-2 w-11/12 sm:w-auto max-w-md z-50 ${
                message.type === "success"
                  ? "bg-red-100 text-red-800"
                  : message.type === "warning"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              <FontAwesomeIcon
                icon={
                  message.type === "success"
                    ? faCheckCircle
                    : message.type === "warning"
                    ? faClock
                    : faTimesCircle
                }
                className="h-5 w-5"
              />
              <span>{message.text}</span>
            </div>
          )}
        </AnimatePresence>

        {error && !pesanan ? (
          <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
            <div className="text-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-8 w-8 text-red-600 mb-4"
              />
              <p className="text-red-600 text-base font-medium">{error}</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden">
            {/* Header Section */}
            <div className="text-center">
              <FontAwesomeIcon
                icon={
                  eventData?.type === "trail_running"
                    ? faRunning
                    : eventData?.type === "membership"
                    ? faUser
                    : eventData?.type === "shuttle"
                    ? faBus
                    : faChalkboardTeacher
                }
                className="h-12 w-12 text-red-600 my-4"
              />
              <h2 className="text-xl uppercase font-bold text-red-600">
                {eventData?.title || pesanan?.eventId || "Memuat data..."}
              </h2>
              <p className="text-lg font-medium text-red-600 ">
                {eventData?.type === "trail_running"
                  ? "Trail Running"
                  : eventData?.type === "membership"
                  ? "Membership"
                  : eventData?.type === "shuttle"
                  ? "Shuttle Service"
                  : eventData?.type === "seminar"
                  ? "Seminar"
                  : "Memuat data..."}
              </p>
              <div className="mt-1 text-gray-600 text-sm">
                <span>{eventData?.location || "Memuat data..."}</span>
              </div>
            </div>

            {/* Status Section */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-red-600">
                  Status Pesanan
                </h3>
                <span
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-1 rounded-full border ${
                    pesanan?.transaction_status === "settlement"
                      ? "border-red-600 text-red-700"
                      : pesanan?.transaction_status === "pending"
                      ? "border-yellow-500 text-yellow-600"
                      : "border-gray-500 text-gray-600"
                  }`}
                >
                  <FontAwesomeIcon
                    icon={
                      pesanan?.transaction_status === "settlement"
                        ? faCheckCircle
                        : pesanan?.transaction_status === "pending"
                        ? faClock
                        : faTimesCircle
                    }
                    className={
                      pesanan?.transaction_status === "settlement"
                        ? "text-red-600"
                        : pesanan?.transaction_status === "pending"
                        ? "text-yellow-500"
                        : "text-gray-500"
                    }
                  />
                  {pesanan?.transaction_status === "settlement"
                    ? "Berhasil"
                    : pesanan?.transaction_status === "pending"
                    ? "Menunggu"
                    : "Memuat data..."}
                </span>
              </div>
              <div className="mt-2 space-y-2 text-gray-600 text-sm">
                {eventData?.type === "shuttle" ? (
                  <>
                    <p>
                      <span className="font-bold text-gray-800">
                        Berangkat:
                      </span>{" "}
                      {pesanan?.pickupPoint && pesanan?.departureTime
                        ? `${pesanan.pickupPoint}, ${formatDate(
                            pesanan.departureTime
                          )}`
                        : pesanan?.pickupPoint || "Tidak diketahui"}
                    </p>
                    <p>
                      <span className="font-bold text-gray-800">Pulang:</span>{" "}
                      {pesanan?.returnPoint && pesanan?.returnTime
                        ? `${pesanan.returnPoint}, ${formatDate(
                            pesanan.returnTime
                          )}`
                        : pesanan?.returnPoint || "Tidak tersedia"}
                    </p>
                  </>
                ) : eventData?.type === "trail_running" ? (
                  <p>
                    <span className="font-bold text-gray-800">Kategori:</span>{" "}
                    {pesanan?.kategori || "Memuat data..."}
                  </p>
                ) : null}
                {(pesanan?.createdAt || pesanan?.transaction_time) && (
                  <p>
                    <span className="font-bold text-gray-800">Transaksi:</span>{" "}
                    {formatDate(
                      pesanan?.createdAt || pesanan?.transaction_time
                    )}
                  </p>
                )}
                <p>
                  <span className="font-bold text-gray-800">Harga:</span> Rp{" "}
                  {pesanan?.harga ? pesanan.harga.toLocaleString() : "0"}
                </p>
              </div>
              {pesanan?.transaction_status === "pending" &&
                pesanan?.va_numbers?.length > 0 && (
                  <div className="mt-4 p-4 bg-slate-100 rounded-xl">
                    <h4 className="text-sm font-bold text-red-600">
                      Detail Pembayaran
                    </h4>
                    <ul className="mt-2 space-y-2 text-sm text-gray-600">
                      {pesanan.va_numbers.map((va, index) => (
                        <li key={index}>
                          <span className="font-medium text-gray-600">
                            {va.bank?.toUpperCase() || "Bank"} VA:
                          </span>{" "}
                          {va.va_number || "Memuat data..."}
                        </li>
                      ))}
                    </ul>
                    {pesanan.expiry_time && (
                      <p className="text-sm text-red-600 mt-2">
                        <span className="font-medium">Batas Waktu:</span>{" "}
                        {formatDate(pesanan.expiry_time)}
                      </p>
                    )}
                  </div>
                )}
            </div>

            {/* Detail Pendaftar Section */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-red-600 mb-4">
                Detail Pendaftar
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: faUser,
                    label: "Nama",
                    value: pesanan?.namaLengkap || "Memuat data...",
                  },
                  {
                    icon: faHashtag,
                    label: "Order ID",
                    value:
                      pesanan?.id?.replace(/^(ORDER-|MEMBERSHIP-|SIM-)/, "") ||
                      "Memuat data...",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-red-50 border border-red-200 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="h-4 w-4 text-red-600"
                      />
                      <span className="text-sm font-bold text-red-600">
                        {item.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-6">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {[
                  /*  {
                    icon: faUser,
                    label: "Email",
                    value: pesanan?.email || "Tidak tersedia",
                  },
                  {
                    icon: faUser,
                    label: "Telepon",
                    value: pesanan?.telepon || "Tidak tersedia",
                  }, */
                ].map((item, index) => (
                  <div
                    key={index}
                    className="p-3 bg-lime-50 border border-lime-200 rounded-xl"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="h-4 w-4 text-red-600"
                      />
                      <span className="text-sm font-bold text-red-600">
                        {item.label}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-6">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
              {/* Bib Number Section */}
              {eventData?.type === "trail_running" && pesanan?.bibNumber && (
                <div className="flex justify-center">
                  <div className="p-4 bg-lime-50 border border-lime-200 rounded-xl max-w-md w-full text-center">
                    {pesanan.bibName && (
                      <p className="text-sm text-gray-600 mb-2">BIB Number</p>
                    )}
                    <div className="text-5xl mb-2 uppercase font-black text-red-600">
                      {pesanan.bibNumber}
                    </div>
                    {pesanan.bibName && (
                      <p className="text-sm text-gray-600 mb-2">
                        BIB Name {pesanan.bibName}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Tombol Bayar Sekarang */}
            <div className="flex justify-center my-4">
              {pesanan?.transaction_status === "pending" &&
                pesanan?.snapToken && (
                  <button
                    onClick={() => handleBayar(pesanan.snapToken)}
                    className="px-4 py-3 rounded-xl text-sm uppercase font-medium bg-red-600 text-white hover:bg-red-700 transition-colors duration-200 w-full"
                    aria-label="Bayar sekarang"
                  >
                    Bayar Sekarang
                  </button>
                )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
});
